'use strict';
const trackingUtils = require('od-modules/utils/tracking.utils');
const tripTypeChecker = require('od-modules/utils/trip_type');
const MIN_DAYS_FOR_LONG_TRIP = 7;

/**
 * Calculates the number of days between two given dates
 *
 * @param {string} firstDateStr The first date in 'YYYY-MM-DD' format
 * @param {string} secondDateStr The second date in 'YYYY-MM-DD' format
 * @return {number} Returns the number of whole days between the two dates
 */
const getDaysBetweenTwoDates = (firstDateStr, secondDateStr) => {
    if (!firstDateStr || !secondDateStr) return NaN;

    // Parsed as UTC because UTC doesn't have DST. JS's Date implementation also ignores leap seconds
    // This means between two UTC dates there will always be an exact number of days
    const firstDateSegments = firstDateStr.split('-');
    const firstDate = Date.UTC(firstDateSegments[0], firstDateSegments[1] - 1, firstDateSegments[2]);

    const secondDateSegments = secondDateStr.split('-');
    const secondDate = Date.UTC(secondDateSegments[0], secondDateSegments[1] - 1, secondDateSegments[2]);

    const dayInMs = 24 * 60 * 60 * 1000;
    return (secondDate - firstDate) / dayInMs;
};

module.exports = {
    getDestinationFromSession: function getDestinationFromSession() {
        const searchData = Odigeo.Utils.parseJson(Odigeo.Session.get('searchParameters'));
        return searchData?.accommodationDestination || searchData?.searchSegments?.[0].destination;
    },

    hasToShowResults: function(data) {
        const {messages, accommodationSearchResultsMetadata} = data;
        const numResults = !Odigeo._.isNull(accommodationSearchResultsMetadata) ?
            accommodationSearchResultsMetadata.numFilteredResults : 0;
        const messagesChecked = messages.length === 0 || messages[0].code === 'DISTANCE_LOCATION_ERROR';
        return (numResults !== 0 && messagesChecked);
    },

    hasToShowDistanceAlert: function(messages) {
        return messages.length === 1 && messages[0].code === 'DISTANCE_LOCATION_ERROR';
    },

    getRequestData: function() {
        const {accommodationSearchRequest} = Odigeo.Utils.parseJson(Odigeo.Session.get('accommodationSearchParameters'));
        return accommodationSearchRequest;
    },

    setPreviousSearchParameters: function() {
        Odigeo.Session.set('oldSearchParameters', Odigeo.Session.get('searchParameters'));
    },

    updateSearchParams: function() {
        const {destination, checkInDate, checkOutDate} = this.getRequestData();
        const searchParameters = Odigeo.Utils.parseJson(Odigeo.Session.get('searchParameters'));
        searchParameters.accommodationDestination = destination;
        searchParameters.checkInDate = checkInDate;
        searchParameters.checkOutDate = checkOutDate;
        Odigeo.Session.set('searchParameters', JSON.stringify(searchParameters));
    },

    getDestination: function() {
        const {accommodationDestination, searchSegments} = Odigeo.Utils.parseJson(Odigeo.Session.get('searchParameters'));
        return accommodationDestination || searchSegments[0].destination;
    },

    getCityNameFromDestination: function(destination) {
        const {city, name} = destination;
        return city ? city : name;
    },

    getCheckOutDate: function(searchParams) {
        const {searchSegments, checkOutDate} = searchParams;
        const isRoundTrip = searchSegments.length > 1;
        return checkOutDate || (isRoundTrip ?
            searchSegments[1].dateStr :
            Odigeo.Utils.getNextDay(searchSegments[0].dateStr));
    },

    getPreviousDestinationAndDates: function() {
        const searchParams = Odigeo.Utils.parseJson(Odigeo.Session.get('oldSearchParameters'));
        return {
            destination: searchParams?.accommodationDestination || searchParams?.searchSegments[0].destination,
            checkInDate: searchParams?.checkInDate || searchParams?.searchSegments[0].dateStr,
            checkOutDate: this.getCheckOutDate(searchParams),
        };
    },

    getSummaryData: function(data) {
        return {
            destination: this.getDestination().name,
            numResults: data.numFilteredResults,
        };
    },

    getNewSearchTrackingSuffix: function(params) {
        const newDestination = params.accommodationDestination || params.searchSegments[0].destination;
        const {checkInDate, checkOutDate, destination} = this.getPreviousDestinationAndDates();
        const areDatesDifferent = checkInDate !== params.checkInDate || checkOutDate !== params.checkOutDate;
        let suffix = areDatesDifferent ? 'dates' : 'same';

        if (destination.name !== newDestination.name) {
            suffix = areDatesDifferent ? 'both' : 'city';
        }

        return suffix;
    },

    replaceUrlParams: function(banners, urlParams) {
        return Odigeo._.each(banners, function(banner, urlKey) {
            if (Odigeo._.get(banner, 'url')) {
                const params = urlParams[urlKey];
                Odigeo._.each(params, function(param, key) {
                    banner.url = banner.url.replace('$' + key + '$', param);
                });
            }
        });
    },

    getHotelDataFromSession: function() {
        const searchParameters = Odigeo.Utils.parseJson(Odigeo.Session.get('searchParameters'));
        const checkInDate = searchParameters.searchSegments[0].dateStr;
        const checkOutDate = this.getCheckOutDate(searchParameters);
        const visitId = Odigeo.SessionScope.visitId;

        return {
            iata: searchParameters.searchSegments[0].destination.iata,
            checkInMonthDay: Odigeo.Utils.formatDate(checkInDate, 'dd', true),
            checkInYearMonth: Odigeo.Utils.formatDate(checkInDate, 'yyyy-mm', true),
            checkOutMonthDay: Odigeo.Utils.formatDate(checkOutDate, 'dd', true),
            checkOutYearMonth: Odigeo.Utils.formatDate(checkOutDate, 'yyyy-mm', true),
            visitId,
        };
    },

    getHotelLinkFromSession: () => {
        const {
            iata,
            checkInMonthDay,
            checkInYearMonth,
            checkOutMonthDay,
            checkOutYearMonth,
            visitId,
        } = this.getHotelDataFromSession();
        const link = Odigeo.UIElements.translations.xsellHomePage.homeHotelTabUrl;

        return Odigeo.Utils.replaceCmsKeyArgs(
            link,
            iata,
            checkInMonthDay,
            checkInYearMonth,
            checkOutMonthDay,
            checkOutYearMonth,
            visitId
        );
    },

    listenClickOnChildIframe(selector, listener) {
        if (!selector) {
            return;
        }

        window.addEventListener('blur', () => {
            setTimeout(() => {
                if (document.activeElement.closest(selector)) {
                    listener();
                }
            }, 0);
        });
    },

    loadImg(url) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        }).catch(() => url);
    },

    getNewGalleryIndex(isPrev, imgIndex, maxIndex) {
        let newIndex = isPrev ? --imgIndex : ++imgIndex;
        if (newIndex < 0) {
            newIndex = maxIndex - 1;
        } else if (newIndex >= maxIndex) {
            newIndex = 0;
        }
        return newIndex;
    },

    onContinueWithoutHotel() {
        const data = Odigeo.Utils.parseJson(Odigeo.Session.get('flightSelectionData'));

        trackingUtils.startMetric('LOAD_SUMMARY_FLIGHTS');
        trackingUtils.trackEvent('continue_next_step', 'continue_without_hotel');

        Odigeo.trigger('footer:hideLegalFooter');
        Odigeo.Session.set('summaryDataLoaded', false);
        Odigeo.UIElements.enhanceLoading($('.waiting_page')).showLoading(['waiting_summary']);

        Odigeo.requireWidget('book-flight-manager', 'book_flight_manager', function(bookflightmanager) {
            bookflightmanager.execute('bookFlightHandler', data);
        });
    },

    onErrorAccommodationSearchRetry(hasDoneRetry, accommodationWidget) {
        if (hasDoneRetry < 2) {
            setTimeout(() => {
                trackingUtils.trackEvent('upsell_widget', 'retry_retrieve_hotel_timeout_' + hasDoneRetry);
                accommodationWidget.execute('getAccommodationResults');
            }, 2000);
            hasDoneRetry++;
            return hasDoneRetry;
        } else {
            trackingUtils.trackEvent('upsell_widget', 'retry_retrieve_hotel_timeout_' + hasDoneRetry);
            hasDoneRetry = 0;
            Odigeo.Router.navigate('passengersinfo', {params: 'redirect=acc_no_results'});
            return hasDoneRetry;
        }
    },

    isLongTrip(itinerarySearchParams = Odigeo.Utils.parseJson(Odigeo.Session.get('searchParameters'))) {
        const {departureDate, returnDate} = this.getItineraryDatesFromSearchParams(itinerarySearchParams);
        return getDaysBetweenTwoDates(departureDate, returnDate) >= MIN_DAYS_FOR_LONG_TRIP;
    },

    isTelesalesMarketingPortal() {
        return Odigeo.SessionScope.mktPortal.toLowerCase() === 'telesales' && Odigeo.AB.isIntermediatePageForOwDesktop;
    },

    getItineraryDatesFromSearchParams(itinerarySearchParams = Odigeo.Utils.parseJson(Odigeo.Session.get('searchParameters'))) {
        const flightSearchSegments = itinerarySearchParams?.searchSegments;
        return {
            departureDate: flightSearchSegments?.[0]?.dateStr,
            returnDate: flightSearchSegments?.[1]?.dateStr,
        };
    },

    canImprovePricePerception(itinerarySearchParams = Odigeo.Utils.parseJson(Odigeo.Session.get('searchParameters'))) {
        const tripType = itinerarySearchParams?.type;
        const isLongTrip = this.isLongTrip(itinerarySearchParams);
        const isAbEnabled = Odigeo.AB.isIpSingleNightOnLongTripsForNonMeta;
        const isRoundTrip = tripTypeChecker.isRoundTrip(tripType);
        const isMeta = Odigeo.SessionScope.isMeta;
        const isTelesales = this.isTelesalesMarketingPortal();
        const conditionsToImprovePricePerception = [
            isAbEnabled,
            isLongTrip,
            isRoundTrip,
            !isMeta,
            !isTelesales,
        ];
        return conditionsToImprovePricePerception.every((condition) => condition);
    },

    getPricePerceptionCheckoutDateStr(checkInDateStr) {
        return Odigeo.Utils.getNextDay(checkInDateStr);
    },

    getPricePerceptionCheckoutDateIfPossible(itinerarySearchParams = Odigeo.Utils.parseJson(Odigeo.Session.get('searchParameters'))) {
        let customCheckoutDate;
        if (this.canImprovePricePerception(itinerarySearchParams)) {
            const {departureDate} = this.getItineraryDatesFromSearchParams(itinerarySearchParams);
            customCheckoutDate = this.getPricePerceptionCheckoutDateStr(departureDate);
        }
        return customCheckoutDate;
    },
};
